import { useSuspenseQuery } from "@apollo/client";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Heading,
  PrimaryFullScreenSpinner,
  StatusUI,
  StripLayout,
  TDProgressBar,
} from "@repo/ui";
import { now } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Duration } from "luxon";
import { Suspense } from "react";
import { PiArrowElbowLeftUpBold } from "react-icons/pi";
import { RiBankFill } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { FundingSection } from "~/components/TermDepositsQueue/funding-section";
import { TermDepositMaturitySection } from "~/components/TermDepositsQueue/maturity-section";
import { OrganisationContextCrumbs } from "~/components/TermDepositsQueue/organisation-context-crumbs";
import { OutgoingTransfersSection } from "~/components/TermDepositsQueue/outgoing-transfers-section";
import { TDDetailsSection } from "~/components/TermDepositsQueue/td-details";
import { graphql } from "~/gql/client";
import type {
  AdminTermDepositDetailPageQuery,
  Currency,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

const nonFailedTransferStatuses = [
  "IN_PROGRESS",
  "REQUESTED",
  "COMPLETED",
  "PENDING",
  "SCHEDULED",
] as const;

type TransferStatus =
  | "requested"
  | "scheduled"
  | "processing"
  | "processed"
  | "settled"
  | "cancelled"
  | "failed"
  | "voided";

type RolloverDetails = {
  id: string;
  sourceTermDepositId: string;
  destinationTermDepositId: string;
  name: string;
  provider?: string;
  durationDays: Decimal;
  rate: Decimal;
  principal?: Decimal;
  start: Date;
  end: Date;
};

export type TermDepositQueueItemData = {
  id: string;
  name: string;
  currency: Currency;
  principalAmount: Decimal;
  status:
    | "SCHEDULED"
    | "ACTIVE"
    | "PENDING"
    | "MATURED"
    | "CLOSED"
    | "HOLDING"
    | "DISABLED"
    | "DELETED"
    | "MATURING";
  provider: string;
  providerLogoUrl?: string | null;
  organisationName?: string | null;
  organisationId: string;
  orgHasOneBusinessEntity: boolean;
  businessEntityName: string;
  businessEntityId: string;
  rate: Decimal;
  feeRate?: Decimal | null;
  startUTC: Date;
  endUTC: Date;
  balance: {
    amount: Decimal;
    currency: Currency;
  };
  inFlightNet: {
    amount: Decimal;
    currency: Currency;
  };
  maturityInstructions?:
    | { kind: "rollover"; updatedAt: Date }
    | { kind: "rollover_principal"; assetId: string; updatedAt: Date }
    | { kind: "transfer"; assetId: string; updatedAt: Date };
  amounts?: {
    principal: Decimal;
    yield: Decimal;
    totalValueAtMaturity: Decimal;
  };
  depositDestination?: {
    abn: string;
    address: string;
  };
};

export type LoaderData = {
  rolloverQuotes: {
    id: string;
    productId: string;
    productName: string;
    durationDays: Decimal;
    apy: Decimal;
  }[];
  rolloverTo?: RolloverDetails;
  rolloverFrom?: RolloverDetails;
  transferOptions: {
    id: string;
    name: string;
    balance: Decimal;
    currency: Currency;
    provider?: string;
    shortReference: string;
    termDepositDetails?: {
      start: Date;
      end: Date;
    };
  }[];
  transfers: {
    id: string;
    amount: Decimal;
    name: string;
    date: Date;
    status: TransferStatus;
    providerName?: string;
  }[];
  outgoingTransfers: {
    id: string;
    amount: Decimal;
    name: string;
    date: Date;
    status: TransferStatus;
    providerName?: string;
  }[];
  deposits: {
    id: string;
    amount: Decimal;
    date: Date;
    completed: boolean;
  }[];
  transferFromOptions: {
    id: string;
    name: string;
    balance: Decimal;
    currency: Currency;
    provider?: string;
  }[];
  yieldTransactions: {
    id: string;
    amount: Decimal;
    date: Date;
    currency: Currency;
    transactionId: string;
  }[];
};

const detailQueryDocument = graphql(`
  query AdminTermDepositDetailPage(
    $termDepositId: ID!
    $incomingTransferFilter: TransfersFilter!
    $outgoingTransferFilter: TransfersFilter!
    $yieldTransactionFilter: TransactionPageFilter!
  ) {
    termDeposit: asset(id: $termDepositId) {
      __typename
      ... on TermDepositAsset {
        id
        shortRef
        name
        currency
        status
        apy
        feeRate
        start
        end
        providerName
        logoUrl
        principleAmount {
          amount
        }
        estimatedValue {
          amount
          currency
        }
        inflightAmount {
          amount
          currency
        }
        businessEntity {
          id
          name
          organisation {
            id
            name
            businessEntities {
              id
            }
          }
          assets(includeInactive: true) {
            __typename
            id
            name
            currency
            status
            providerName
            estimatedValue {
              amount
              currency
            }
            ... on TermDepositAsset {
              start
              end
            }
          }
        }
        maturityInstructions {
          __typename
          ... on TermDepositMaturityInstructionsTransfer {
            toAsset {
              id
            }
          }
          ... on TermDepositMaturityInstructionsRolloverPrinciple {
            toAsset {
              id
            }
          }
        }
      }
    }

    transfersIn: transfers(first: 100, filter: $incomingTransferFilter) {
      transfers {
        id
        requestedForDate
        status
        currencyAmount {
          amount
          currency
        }
        sourceAsset {
          id
          name
          providerName
        }
      }
    }

    transfersOut: transfers(first: 100, filter: $outgoingTransferFilter) {
      transfers {
        id
        requestedForDate
        status
        currencyAmount {
          amount
          currency
        }
        destinationAsset {
          id
          name
          providerName
        }
      }
    }

    yieldTransactions: transactions(first: 100, filter: $yieldTransactionFilter) {
      transactions {
        id
        effectiveAt
        status
        type
        currencyAmount {
          amount
          currency
        }
      }
    }

    deposits(first: 100, order: DATE_DESC) {
      deposits {
        id
        date
        amount {
          amount
          currency
        }
        destinationAsset {
          id
        }
        ledgerTransactions {
          id
          effectiveAt
          status
        }
      }
    }

    products {
      id
      name
      ... on TermDepositProduct {
        provider {
          id
          name
        }
        rateQuotes {
          id
          duration
          rate
        }
      }
    }
  }
`);

function mapTermDepositDetails(
  asset: Extract<
    NonNullable<AdminTermDepositDetailPageQuery["termDeposit"]>,
    { __typename: "TermDepositAsset" }
  >,
): TermDepositQueueItemData {
  const principal = asset.principleAmount?.amount;
  const totalValueAtMaturity = asset.estimatedValue?.amount;
  const estimatedYield =
    principal && totalValueAtMaturity
      ? totalValueAtMaturity.minus(principal)
      : undefined;

  const syntheticStatus =
    now().plus({ days: 14 }).toJSDate() >= asset.end &&
    asset.status === "ACTIVE"
      ? ("MATURING" as const)
      : asset.status;

  return {
    id: asset.id,
    name: asset.name,
    currency: asset.currency,
    principalAmount: principal ?? new Decimal(0),
    status: syntheticStatus,
    provider: asset.providerName ?? "",
    providerLogoUrl: asset.logoUrl,
    organisationName:
      asset.businessEntity?.organisation?.name ?? "Organisation",
    organisationId: asset.businessEntity?.organisation?.id ?? "",
    orgHasOneBusinessEntity:
      (asset.businessEntity?.organisation?.businessEntities?.length ?? 0) <= 1,
    businessEntityName: asset.businessEntity?.name ?? "",
    businessEntityId: asset.businessEntity?.id ?? "",
    rate: asset.apy ?? new Decimal(0),
    feeRate: asset.feeRate ?? null,
    startUTC: asset.start,
    endUTC: asset.end,
    balance: {
      amount: asset.estimatedValue?.amount ?? new Decimal(0),
      currency: asset.estimatedValue?.currency ?? asset.currency,
    },
    inFlightNet: {
      amount: asset.inflightAmount?.amount ?? new Decimal(0),
      currency: asset.inflightAmount?.currency ?? asset.currency,
    },
    maturityInstructions:
      asset.maturityInstructions?.__typename ===
      "TermDepositMaturityInstructionsRollover"
        ? { kind: "rollover", updatedAt: new Date() }
        : asset.maturityInstructions?.__typename ===
              "TermDepositMaturityInstructionsRolloverPrinciple" &&
            asset.maturityInstructions.toAsset?.id
          ? {
              kind: "rollover_principal",
              assetId: asset.maturityInstructions.toAsset.id,
              updatedAt: new Date(),
            }
          : asset.maturityInstructions?.__typename ===
                "TermDepositMaturityInstructionsTransfer" &&
              asset.maturityInstructions.toAsset?.id
            ? {
                kind: "transfer",
                assetId: asset.maturityInstructions.toAsset.id,
                updatedAt: new Date(),
              }
            : undefined,
    amounts:
      principal && estimatedYield && totalValueAtMaturity
        ? {
            principal,
            yield: estimatedYield,
            totalValueAtMaturity,
          }
        : undefined,
  };
}

function TDDetailPane({
  termDepositDetails,
  deposits,
  transfers,
  outgoingTransfers,
  transferOptions,
  rollover,
  rolloverFrom,
  rolloverQuotes,
  transferFromOptions,
  yieldTransactions,
}: {
  rollover: LoaderData["rolloverTo"];
  rolloverFrom: LoaderData["rolloverFrom"];
  transferFromOptions: LoaderData["transferFromOptions"];
  transferOptions: LoaderData["transferOptions"];
  termDepositDetails: TermDepositQueueItemData;
  deposits: LoaderData["deposits"];
  transfers: LoaderData["transfers"];
  outgoingTransfers: LoaderData["outgoingTransfers"];
  rolloverQuotes: LoaderData["rolloverQuotes"];
  yieldTransactions: LoaderData["yieldTransactions"];
}) {
  const isActiveOrMatured =
    termDepositDetails.status === "ACTIVE" ||
    termDepositDetails.status === "MATURING" ||
    termDepositDetails.status === "MATURED";

  return (
    <Card key={termDepositDetails.id} className="shadow-elevate-drop-shadow">
      <CardHeader className="grid gap-6">
        <OrganisationContextCrumbs
          organisationName={
            termDepositDetails.organisationName ?? "Organisation"
          }
          orgHasOneBusinessEntity={termDepositDetails.orgHasOneBusinessEntity}
          currency={termDepositDetails.currency}
        />
        <StripLayout
          iconClassName="place-self-start"
          icon={
            <Avatar variant="square" bordered>
              <AvatarImage
                src={termDepositDetails.providerLogoUrl ?? undefined}
              />
              <AvatarFallback variant="square">
                <RiBankFill />
              </AvatarFallback>
            </Avatar>
          }
          leftContent={
            <div className="grid place-content-start place-items-start gap-2 ">
              <CardTitle className="w-full truncate">
                {termDepositDetails.name}
              </CardTitle>
              <div className="inline-flex items-center gap-2">
                <StatusUI
                  status={
                    termDepositDetails.status.toLowerCase() as Lowercase<
                      typeof termDepositDetails.status
                    >
                  }
                />
                {" • "}
                <TDProgressBar
                  className="whitespace-nowrap text-xs"
                  startDate={termDepositDetails.startUTC}
                  endDate={termDepositDetails.endUTC}
                />
              </div>
            </div>
          }
          leftSubtitle={
            rolloverFrom ? (
              <div className="inline-grid grid-flow-col items-center justify-start gap-2">
                <PiArrowElbowLeftUpBold />
                <Button asChild variant="link-primary" size="sm-link">
                  <Link
                    to={`/term-deposits-queue/${rolloverFrom.sourceTermDepositId}`}
                  >
                    <Heading as="span" type="account">
                      Rolled over from {rolloverFrom.name}
                    </Heading>
                  </Link>
                </Button>
              </div>
            ) : null
          }
          className="flex flex-row items-center gap-4 "
        />
      </CardHeader>

      <CardContent className="grid gap-8">
        <TDDetailsSection termDepositDetails={termDepositDetails} />
        <FundingSection
          yieldTransactions={yieldTransactions}
          termDepositDetails={termDepositDetails}
          deposits={deposits}
          transfers={transfers}
          transferFromOptions={transferFromOptions}
        />
        {isActiveOrMatured ? (
          <TermDepositMaturitySection
            rolloverQuotes={rolloverQuotes}
            rolloverTo={rollover}
            termDepositDetails={termDepositDetails}
            transferOptions={transferOptions}
          />
        ) : null}
        <OutgoingTransfersSection
          termDepositDetails={termDepositDetails}
          outgoingTransfers={outgoingTransfers}
          rollover={rollover}
          transferOptions={transferOptions}
        />
      </CardContent>
    </Card>
  );
}

export const Route = createFileRoute(
  "/__auth/term-deposits-queue_/$termDepositId",
)({
  head: () => opsHead("Term Deposit"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { termDepositId } = Route.useParams();
  if (!termDepositId) {
    throw new Error("termDepositId is required");
  }

  const { data } = useSuspenseQuery(detailQueryDocument, {
    variables: {
      termDepositId,
      incomingTransferFilter: {
        destinationAssetIds: [termDepositId],
        statuses: [...nonFailedTransferStatuses],
      },
      outgoingTransferFilter: {
        sourceAssetIds: [termDepositId],
        statuses: [...nonFailedTransferStatuses],
      },
      yieldTransactionFilter: {
        assetIds: [termDepositId],
        status: ["DRAFT", "PENDING", "POSTED"],
        transactionType: ["YIELD"],
      },
    },
  });

  if (!data.termDeposit || data.termDeposit.__typename !== "TermDepositAsset") {
    throw new Response("Not Found", { status: 404 });
  }

  useOpsTitle(`Term Deposit #${data.termDeposit.shortRef}`);

  const termDepositDetails = mapTermDepositDetails(data.termDeposit);

  const activeAssets = (data.termDeposit.businessEntity?.assets ?? []).filter(
    (asset) => {
      if (!asset) return false;
      if (asset.__typename === "TermDepositAsset") {
        return (
          asset.start < now().plus({ days: 30 }).toJSDate() &&
          asset.end >= now().minus({ days: 30 }).toJSDate()
        );
      }
      return asset.status === "ACTIVE";
    },
  );

  const transferOptions = activeAssets
    .filter((asset) => asset.id !== termDepositId)
    .map((asset) => ({
      id: asset.id,
      name: asset.name,
      balance: asset.estimatedValue?.amount ?? new Decimal(0),
      currency: asset.estimatedValue?.currency ?? asset.currency,
      provider: asset.providerName ?? undefined,
      shortReference: asset.id,
      termDepositDetails:
        asset.__typename === "TermDepositAsset"
          ? {
              start: asset.start,
              end: asset.end,
            }
          : undefined,
    }));

  const transferFromOptions = activeAssets
    .filter((asset) => asset.__typename === "SavingsAsset")
    .map((asset) => ({
      id: asset.id,
      name: asset.name,
      balance: asset.estimatedValue?.amount ?? new Decimal(0),
      currency: asset.estimatedValue?.currency ?? asset.currency,
      provider: asset.providerName ?? undefined,
    }));

  const deposits = (data.deposits?.deposits ?? [])
    .filter((d) => d?.destinationAsset?.id === termDepositId)
    .map((d) => ({
      id: d.id,
      amount: d.amount?.amount ?? new Decimal(0),
      date: d.date,
      completed:
        d.ledgerTransactions?.some(
          (t) => t.effectiveAt <= new Date() && t.status === "POSTED",
        ) ?? false,
    }));

  const transfers = (data.transfersIn?.transfers ?? []).flatMap((t) =>
    !t || !t.sourceAsset || !t.currencyAmount || !t.status
      ? []
      : {
          id: t.id,
          date: t.requestedForDate,
          amount: t.currencyAmount.amount,
          status: t.status.toLowerCase() as
            | "requested"
            | "scheduled"
            | "processing"
            | "processed"
            | "settled"
            | "cancelled"
            | "failed"
            | "voided",
          name: t.sourceAsset.name,
          providerName: t.sourceAsset.providerName ?? undefined,
        },
  );

  const outgoingTransfers = (data.transfersOut?.transfers ?? []).flatMap((t) =>
    !t || !t.destinationAsset || !t.currencyAmount || !t.status
      ? []
      : {
          id: t.id,
          date: t.requestedForDate,
          amount: t.currencyAmount.amount,
          status: t.status.toLowerCase() as
            | "requested"
            | "scheduled"
            | "processing"
            | "processed"
            | "settled"
            | "cancelled"
            | "failed"
            | "voided",
          name: t.destinationAsset.name,
          providerName: t.destinationAsset.providerName ?? undefined,
        },
  );

  const rolloverQuotes = (data.products ?? [])
    .filter((p) => p?.__typename === "TermDepositProduct")
    .filter((p) => p.provider?.name === termDepositDetails.provider)
    .flatMap((p) =>
      p.rateQuotes.map((q) => ({
        id: q.id,
        productId: p.id,
        productName: p.name,
        durationDays: new Decimal(
          typeof q.duration === "string"
            ? Duration.fromISO(q.duration).as("days")
            : q.duration.as("days"),
        ),
        apy: q.rate,
      })),
    );

  const yieldTransactions = (
    data.yieldTransactions?.transactions ?? []
  ).flatMap((transaction) =>
    transaction?.type === "YIELD" && transaction.currencyAmount
      ? {
          id: transaction.id,
          transactionId: transaction.id,
          amount: transaction.currencyAmount.amount,
          date: transaction.effectiveAt,
          currency: transaction.currencyAmount.currency,
        }
      : [],
  );

  return (
    <AdminPageContainer>
      <AdminPageHeader title={`Term Deposit #${data.termDeposit.shortRef}`} />
      <div className="grid gap-4">
        <div>
          <Button asChild variant="secondary">
            <Link to="/term-deposits-queue">Back to queue</Link>
          </Button>
        </div>
        <TDDetailPane
          key={termDepositDetails.id}
          yieldTransactions={yieldTransactions}
          transferFromOptions={transferFromOptions}
          rolloverQuotes={rolloverQuotes}
          rollover={undefined}
          rolloverFrom={undefined}
          transferOptions={transferOptions}
          termDepositDetails={termDepositDetails}
          deposits={deposits}
          transfers={transfers}
          outgoingTransfers={outgoingTransfers}
        />
      </div>
    </AdminPageContainer>
  );
}
